// Header.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ShieldCheckIcon, 
  UserCircleIcon, 
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import AlertBadge from '../Teacher/AlertBadge';

const Header = () => {
  const { user, logout, isStudent, isTeacher } = useAuth();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  
  // 역할 확인 함수 호출
  const userIsStudent = isStudent();
  const userIsTeacher = isTeacher();

  const handleLogout = () => {
    logout();
    setShowProfileMenu(false);
  };

  return (
    <header className="bg-white shadow-lg border-b-4 border-primary-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* 로고 및 브랜드 */}
          <div className="flex items-center space-x-3">
            <motion.div
              whileHover={{ rotate: 10 }}
              className="flex-shrink-0"
            >
              <ShieldCheckIcon className="h-10 w-10 text-primary-500" />
            </motion.div>
            <div>
              <h1 className="text-2xl font-bold gradient-text">
                SafeAI Kids
              </h1>
              <p className="text-xs text-gray-500">
                완도교육지원청/Upstage 지원
              </p>
            </div>
          </div>

          {/* 네비게이션 메뉴 제거됨 */}

          {/* 사용자 정보 및 메뉴 */}
          {user && (
            <div className="flex items-center space-x-4">
              {/* 역할 배지 */}
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                userIsStudent 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                <div className="flex items-center space-x-1">
                  {userIsStudent ? (
                    <>
                      <AcademicCapIcon className="h-4 w-4" />
                      <span>학생</span>
                    </>
                  ) : userIsTeacher ? (
                    <>
                      <ShieldCheckIcon className="h-4 w-4" />
                      <span>선생님</span>
                    </>
                  ) : null}
                </div>
              </div>

              {/* 위험 신호 알림 버튼 (교사만) */}
              {userIsTeacher && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/teacher/alerts')}
                  className="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg"
                  title="위험 신호 알림"
                >
                  <BellIcon className="h-6 w-6" />
                  <span className="absolute -top-1 -right-1 text-xs">
                    <AlertBadge />
                  </span>
                </motion.button>
              )}

              {/* 프로필 메뉴 */}
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg p-2"
                >
                  <UserCircleIcon className="h-8 w-8" />
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium">{user.username}</p>
                    <p className="text-xs text-gray-500">
                      {user.schoolName && `${user.schoolName}`}
                      {user.gradeLevel && ` • ${user.gradeLevel}학년`}
                    </p>
                  </div>
                </motion.button>

                {/* 드롭다운 메뉴 */}
                <AnimatePresence>
                  {showProfileMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 z-50"
                    >
                      <div className="py-1">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">
                            {user.username}
                          </p>
                          <p className="text-xs text-gray-500">
                            {user.email}
                          </p>
                        </div>
                        
                        <button
                          onClick={() => {
                            navigate('/profile');
                            setShowProfileMenu(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Cog6ToothIcon className="h-4 w-4 mr-3" />
                          내 프로필
                        </button>
                        
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                          로그아웃
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 안전 상태 표시줄 */}
      <div className="bg-safe-50 border-t border-safe-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-2">
            <div className="flex items-center space-x-2 text-safe-700">
              <ShieldCheckIcon className="h-4 w-4" />
              <span className="text-sm font-medium">
                🛡️ 안전한 AI 환경에서 학습하고 있습니다
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
